import "./styles/index.css";
import { createClientApp, kernelEmbeds } from "@atomic/app-shell";
import { DigitalTwinEmbed } from "@atomic/digital-twin-adapter/react";
import { LinearEmbed } from "@atomic/linear-adapter/react";
import { UserAdminEmbed } from "@atomic/user-admin-adapter/react";
import { APP_ENV } from "./app_env";
import { COMPANY_CODE } from "./client_identity";
import { useNucleusHost } from "./nucleusHost";
import { TOOL_VARIABLES } from "./nucleusToolVariables";
import { LmntProviders } from "./providers";
import { ENGINE_PAGES } from "./enginePages";
import { ROUTE_REGISTRY_EXTRAS } from "./routeRegistry";
import { FEATURES, NAV_EXTRAS, TOOL_OVERRIDES } from "./rail";
import { PRODUCT_ROUTES } from "./routes";

/**
 * Local dev only: the embeds a developer has pointed at a `vite dev` of their
 * core package on this machine (see .env.example). Only the variables that are
 * set become keys, so with no .env.local — the default state — the shell sees
 * an empty map and frames the minted page. The master-data and BOM Explorer
 * overrides are read by their own LMNT wrappers (embeds/local_embed_url.ts).
 */
function localEmbedUrls(): Partial<Record<"digital-twin", string>> {
  const urls = { "digital-twin": import.meta.env.VITE_DIGITAL_TWIN_EMBED_URL };
  return Object.fromEntries(Object.entries(urls).filter(([, url]) => Boolean(url)));
}

/**
 * The app is the client app shell (@atomic/app-shell) plus LMNT's own pages.
 * The shell owns sign-in and the session (its `fetchWithToken` is what the
 * Apollo client sends through — providers.tsx), the rail and the account menu,
 * the Nucleus dock mount and its route registry, feedback, activity logging,
 * `/digital-twin`, `/admin` (core's user-admin surface, View As beside it for
 * Atomic staff) and the full-page `/nucleus`. This file is the manifest, the
 * adapters it installs, the Engine tiles, and LMNT's routes (routes.tsx).
 *
 * Features: Data, Reporting, Digital Twin and Nucleus are live. Tracker,
 * Allocation and Linear stay off the rail (the shell's default for them) —
 * LMNT reaches /trackers, /allocation-engine and /linear by direct navigation,
 * through its own routes. Docs is routed but has no tile (`toolOverrides`), as
 * the old rail had it. Materials is the Engine module still to come.
 *
 * What the rail no longer carries: the "secret tile" dev-unlock combo the old
 * Sidebar could camouflage a module behind (five quick clicks on a locked
 * tile). No tile declared one, so nothing is lost today; a shell that wants it
 * back needs a per-tile render hook (candidate app-shell change).
 */
createClientApp({
  manifest: {
    companyCode: COMPANY_CODE,
    displayName: "LMNT",
    logo: "/images/lmnt-logo.png",
    homePath: "/data",
    features: FEATURES,
    // Nucleus is on for every LMNT user (2026-10-08): the rail tile, the dock and
    // /nucleus admit all three tiers. The gateway (NUCLEUS_ENABLED_TIERS from SSM
    // nucleus/enabled-tiers) and core (the lmnt registry entries) hold the same list.
    nucleusTiers: ["ROOT", "ADMIN", "DATA_USER"],
    // User writes are core's (the user-admin embed); the shell's AdminPage
    // mounts it over the kernel's `getUserAdminEmbedToken`. Not a role gate:
    // the shell shows User Admin to ADMIN and ROOT (its `isFullApp`), and the
    // embed lets an ADMIN invite the non-root roles, as LMNT's own page did.
    userAdministration: "staff-only",
  },
  env: APP_ENV,
  atomicLogo: "/atomic-logo-blue.png",
  product: {
    // Only the adapters the shell's own pages mount. The master-data,
    // queryviz, tracker and allocation adapters are mounted by LMNT's
    // wrappers (routes.tsx says why), which mint through Apollo as before —
    // registering them here would make the shell add its generic /data,
    // /reporting and friends ahead of those wrappers.
    embeds: kernelEmbeds(
      { "digital-twin": DigitalTwinEmbed, linear: LinearEmbed, "user-admin": UserAdminEmbed },
      {},
      // Local dev only: a `vite dev` of an embed's core package on this
      // machine, framed in place of the minted page. From the gitignored
      // .env.local; the shell honours a local origin only, and a production
      // build drops it.
      import.meta.env.DEV ? localEmbedUrls() : {},
      import.meta.env,
    ),
    routes: PRODUCT_ROUTES,
    // The Engine tiles and the tile overrides: rail.ts, which the route
    // registry test builds its nav from too.
    navExtras: NAV_EXTRAS,
    toolOverrides: TOOL_OVERRIDES,
    routeRegistry: ROUTE_REGISTRY_EXTRAS,
    // The pages that show Replenishment runs, with the controls Nucleus may set on
    // them (enginePages.ts); the shell merges each onto its registry entry.
    enginePages: ENGINE_PAGES,
    // The Nucleus dock is core's standard one (@atomic/app-shell mounts it); LMNT adds
    // only what this app knows — the pull sources behind `@`, its /demand report host,
    // unsaved work (nucleusHost.ts). The run on the page is published by the run
    // selector (useRunSelector), and the shell's dock sends it.
    nucleus: { toolVariables: TOOL_VARIABLES, useHost: useNucleusHost },
    wrap: (app) => <LmntProviders>{app}</LmntProviders>,
  },
}).mount();
